import { useMatches, useNavigate, useParams } from 'react-router-dom'
import {
  Container,
  PageSubtitle,
  PageTitle,
  PageHeaderBox,
  TextHeaderBox,
  ExceptionContainer,
  AdvanceComponent,
  StyledForm,
} from './styles'
import { DashboardDivisor, DashboardOriginSelector } from 'bd-web-components'
import { ReactElement, useEffect, useState } from 'react'
import Loader from '../../components/Loader'
import { GRAY_SCALE_08 } from 'bd-web-components/src/utils/dash-colors'
import {
  AdvanceBalanceDistributionProps,
  useAdvanceBalanceDistribution,
} from '../../hooks/advance-balance-distribution'
import { CustomModal } from '../../components/CustomModal'
import UseTerms from '../../components/UseTerm'
import { ConfirmationModal } from '../../components/ConfirmationModal'
import { SucessOrFailModal } from '../../components/SucessOrFailModal'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import currencyFormatter from 'currency-formatter'
import { useAdvanceSimulation } from '../../hooks/advance-simulation'
import useSWRMutation from 'swr/mutation'
import { useAdvanceRequest } from '../../hooks/advance-request'
import { toast } from 'react-toastify'
import { AdvanceDesktop } from './advance-desktop'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { AdvanceMobile } from './advance-mobile'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import { useAuth, useGuard } from '../../hooks/auth'
import { getOrigin } from '../../utils/getOriginData'
import { errorRedirect } from '../../utils/redirect'
import { Breadcrumb } from '../../components/Breadcrumb'
import {
  AdvanceDebitsNotice,
  AdvanceSimulationOutcome,
} from '../../components/AdvanceDebitsNotice'
import { EventDebits } from '../../types/event-debits'

export interface AdvanceProps {
  totalAdvancedRequested: string
  priceOfAdvance: number
  totalAdvanced: number
  advanceOrigin: string
  termsAndConditions: boolean
}

interface CustomMgsProp {
  title: string
  subtitle: string
}

export interface ConfirmationProps {
  customSuccess: CustomMgsProp
  customError: CustomMgsProp
}

const confirmationSettedMsg: ConfirmationProps = {
  customSuccess: {
    title: 'Antecipação Solicitada com Sucesso!',
    subtitle: 'Agora é só aguardar que vamos processar sua solicitação.',
  },
  customError: {
    title: 'Erro ao Solicitar Antecipação!',
    subtitle: 'Tente novamente!',
  },
}

export function BalanceAdvance(): ReactElement {
  window.document.title = 'Solicitar Antecipação'
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const authContext = useAuth()
  const { authHookWithEvent } = authContext

  // verify if user has this event
  if (!LOCAL_EXECUTION) {
    authHookWithEvent(id!, navigate)
  }

  // verify if user has permission to acess
  useGuard(['DP6'])

  // -----------------------------------------------------------------
  const advanceBalanceDistribution = useAdvanceBalanceDistribution(Number(id))
  const [advanceLimits, setAdvanceLimits] =
    useState<AdvanceBalanceDistributionProps[] | null>(null)
  const [eventDebits, setEventDebits] = useState<EventDebits | undefined>(
    undefined
  )
  const [isTermModal, setIsTermModal] = useState<boolean>(false)
  const [selected, setSelected] = useState<boolean>(true)
  const [advanceLimit, setAdvanceLimit] = useState<number>(0)
  const [origin, setOrigin] = useState<string>('WEB')
  const [loading, setLoading] = useState<boolean>(false)
  const [isSimulation, setIsSimulation] = useState<boolean>(true)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null)
  const [confirmationMsg, setConfirmationMsg] = useState<ConfirmationProps>(
    confirmationSettedMsg
  )
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [code2fa, setCode2fa] = useState<string>('')
  const isMobile = useMediaQuery('(max-width: 768px)')

  let matches = useMatches()
  let crumbs = matches
    .filter((match: any) => Boolean(match.handle?.crumb))
    .map((match: any) => {
      return {
        name: match.handle.crumb(match.data),
        path: match.pathname,
      }
    })

  const initial: AdvanceProps = {
    totalAdvancedRequested: '',
    priceOfAdvance: 0,
    totalAdvanced: 0,
    advanceOrigin: origin,
    termsAndConditions: false,
  }

  const totalAdvancedRequestedObject = Yup.number()
    .typeError('Use apenas números ou virgula')
    .required('Requerido - Use apenas números ou virgula')
    .max(advanceLimit, 'Valor insuficiente para antecipação')

  const simulationValidation = Yup.object().shape({
    totalAdvancedRequested: totalAdvancedRequestedObject,
  })

  const advanceValidation = Yup.object().shape({
    totalAdvancedRequested: totalAdvancedRequestedObject,
    priceOfAdvance: Yup.number().required('Requerido'),
    totalAdvanced: Yup.string().required('Requerido'),
    termsAndConditions: Yup.bool().oneOf([true], ''),
  })

  const useFormikProps = useFormik({
    initialValues: initial,
    enableReinitialize: true,
    validationSchema: isSimulation ? simulationValidation : advanceValidation,
    onSubmit: async (
      values: AdvanceProps,
      { setSubmitting, resetForm }: any
    ) => {
      values.advanceOrigin = origin
      setLoading(true)
      if (isSimulation) {
        try {
          const result = await advanceSimulationTrigger()
          const { data } = result
          if (data.success) {
            const { advance } = data.data
            values.priceOfAdvance = advance.priceOfAdvance
            values.totalAdvanced = advance.totalAdvanced.toString()
            setIsSimulation(false)
            setLoading(false)
          }
        } catch (error: any) {
          toast.error('Um erro aconteceu, tente novamente')
          setIsSimulation(true)
          setLoading(false)
          useFormikProps.resetForm()
        }
      } else {
        setIsOpen(true)
        setLoading(false)
      }
    },
  })

  const { useAdvanceSimulationFetcher } = useAdvanceSimulation(
    id,
    useFormikProps.values
  )
  const { trigger: advanceSimulationTrigger }: any = useSWRMutation(
    'advanceSimulationSWR',
    useAdvanceSimulationFetcher,
    { revalidate: false }
  )

  const { useAdvanceRequestFetcher } = useAdvanceRequest(
    id,
    useFormikProps.values,
    code2fa
  )
  const { trigger: useAdvanceRequestTrigger }: any = useSWRMutation(
    'advanceRequestSWR',
    useAdvanceRequestFetcher,
    { revalidate: false }
  )

  const handlerChangeOrigin = (e: any) => {
    const value = e.target.textContent
    if (value === 'Web a Vencer' && selected === false) {
      setIsSimulation(true)
      useFormikProps.resetForm()
      setSelected(true)
    }
    if (value !== 'Web a Vencer' && selected === true) {
      setIsSimulation(true)
      useFormikProps.resetForm()
      setSelected(false)
    }
  }

  useEffect(() => {
    if (advanceBalanceDistribution.balance !== undefined) {
      const data = advanceBalanceDistribution.balance.data.data.balance
      setAdvanceLimits(data)
      setEventDebits(advanceBalanceDistribution.balance.data.data.eventDebits)
    }
    if (advanceBalanceDistribution.isError) {
      const data = advanceBalanceDistribution.isError.response?.data as {
        message: string
      }
      errorRedirect(
        '/error',
        {
          message: data?.message,
          tittle: 'Não permitido',
        },
        navigate
      )
    }
  }, [
    advanceBalanceDistribution.isError,
    advanceBalanceDistribution.balance,
    navigate,
  ])

  useEffect(() => {
    if (advanceLimits !== null) {
      const originObject: any = getOrigin(selected, advanceLimits)
      const { advanceLimit: value, advanceOrigin } = originObject
      setAdvanceLimit(value)
      setOrigin(advanceOrigin)
    }
  }, [selected, advanceLimits])

  // configure data to show
  const isError = Boolean(
    useFormikProps.errors.totalAdvancedRequested !== undefined &&
    useFormikProps.touched.totalAdvancedRequested
  )
  const disabledControl = Boolean(
    useFormikProps.errors.totalAdvancedRequested === undefined &&
    useFormikProps.touched.totalAdvancedRequested
  )
  const disabledAdvanceControl =
    useFormikProps.values.termsAndConditions === true
  const advanceLimitFormatted = currencyFormatter.format(advanceLimit, {
    code: 'BRL',
  })
  const priceOfAdvance = currencyFormatter.format(
    Number(useFormikProps.values.priceOfAdvance),
    { code: 'BRL' }
  )
  const totalAdvanced = currencyFormatter.format(
    Number(useFormikProps.values.totalAdvanced),
    { code: 'BRL' }
  )
  const firstColumn = Number(useFormikProps.values.totalAdvancedRequested) * 1
  const secoundColumn = 100 * Number(useFormikProps.values.priceOfAdvance)
  const percentageOfAdvance =
    Math.floor((secoundColumn / firstColumn) * 100) / 100
  const advanceTariff = `${
    isNaN(percentageOfAdvance) ? 0 : percentageOfAdvance
  }%`

  const goAheadUrl = `/events/${id}/withdraw/requests/new`
  const rollBackUrl = `/events/${id}/withdraw/requests/list`

  const currentOriginBalance = advanceLimits?.find(
    (item) => item.advanceOrigin === origin
  )

  return (
    <>
      <Container>
        <Breadcrumb paths={crumbs} />
        <PageHeaderBox>
          <TextHeaderBox>
            <PageTitle> Solicitar Antecipação</PageTitle>
            <PageSubtitle>
              Tenha seu saldo disponível na hora! Mas, lembre-se: taxa de
              antecipação se aplica proporcional ao valor antecipado.
            </PageSubtitle>
          </TextHeaderBox>
        </PageHeaderBox>
        <DashboardDivisor
          marginTop={20}
          marginBottom={42}
          marginTopMobile={32}
          marginBottomMobile={0}
          color={GRAY_SCALE_08}
        />
        {advanceLimits === null && (
          <ExceptionContainer height={400} margin={false}>
            <Loader />
          </ExceptionContainer>
        )}
        {advanceLimits !== null && (
          <AdvanceComponent>
            <AdvanceDebitsNotice
              debits={eventDebits}
              advanceToUnlock={currentOriginBalance?.advanceToUnlock}
            />
            <DashboardOriginSelector
              originA={'Web a Vencer'}
              originB={'TEF - Crédito a Vencer'}
              selected={selected}
              setSelected={(e: any) => handlerChangeOrigin(e)}
            />
            <StyledForm onSubmit={useFormikProps.handleSubmit}>
              {!isMobile && (
                <AdvanceDesktop
                  useFormikProps={useFormikProps}
                  advanceLimitFormatted={advanceLimitFormatted}
                  isError={isError}
                  isSimulation={isSimulation}
                  priceOfAdvance={priceOfAdvance}
                  advanceTariff={advanceTariff}
                  disabledControl={disabledControl}
                  loading={loading}
                  setIsSimulation={setIsSimulation}
                  disabledAdvanceControl={disabledAdvanceControl}
                  setIsTermModal={setIsTermModal}
                />
              )}
              {isMobile && (
                <AdvanceMobile
                  useFormikProps={useFormikProps}
                  advanceLimitFormatted={advanceLimitFormatted}
                  isError={isError}
                  totalAdvanced={totalAdvanced}
                  isSimulation={isSimulation}
                  priceOfAdvance={priceOfAdvance}
                  advanceTariff={advanceTariff}
                  disabledControl={disabledControl}
                  loading={loading}
                  setIsSimulation={setIsSimulation}
                  disabledAdvanceControl={disabledAdvanceControl}
                  setIsTermModal={setIsTermModal}
                />
              )}
            </StyledForm>
            <AdvanceSimulationOutcome
              debits={eventDebits}
              netAdvanced={Number(useFormikProps.values.totalAdvanced)}
              visible={!isSimulation}
            />
          </AdvanceComponent>
        )}
        <ConfirmationModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          setCode={setCode2fa}
          setIsSuccess={setIsSuccess}
          setConfirmationMsg={setConfirmationMsg}
          setIsConfirmationOpen={setIsConfirmationOpen}
          submitResponse={useAdvanceRequestTrigger}
        />
        <SucessOrFailModal
          isOpen={isConfirmationOpen}
          isSuccess={isSuccess}
          goAhead={true}
          goAheadText={'Solicitar Saque'}
          goAheadUrl={goAheadUrl}
          customSuccess={confirmationMsg.customSuccess}
          customError={confirmationMsg.customError}
          setIsOpen={setIsConfirmationOpen}
          rollBackUrl={rollBackUrl}
        />
        <CustomModal isOpen={isTermModal}>
          <UseTerms setIsOpen={setIsTermModal} />
        </CustomModal>
      </Container>
    </>
  )
}
